import { useState } from 'react';
import { ChevronRight, RefreshCw, Menu, Bluetooth, BluetoothOff } from 'lucide-react';
import { useDeviceStatus } from '../../stores/systemStore';
import type { MenuId } from '../../stores/uiStore';
import { useTranslation } from '../../locales';
import type { TranslationKeys } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';

/**
 * TopBar — glassy header with breadcrumb + device chip + sync.
 * 반응형: 모바일에서 padding 축소 (CSS .tb @media), breadcrumb 숨김.
 */
interface TopBarProps {
  activeMenu: MenuId;
  onMenuToggle?: () => void;
}

type SidebarItemKey = keyof Pick<
  TranslationKeys['sidebar'],
  'dashboard' | 'deviceManager' | 'visualizer' | 'dataCenter' | 'streamProcess' | 'documents' | 'settings'
>;

const MENU_TKEY: Record<MenuId, SidebarItemKey> = {
  engine:           'dashboard',
  linkband:         'deviceManager',
  visualizer:       'visualizer',
  datacenter:       'dataCenter',
  'stream-process': 'streamProcess',
  cloudmanager:     'documents',
  settings:         'settings',
};

export function TopBar({ activeMenu, onMenuToggle }: TopBarProps) {
  const { isConnected, deviceName, batteryLevel, currentDeviceId } = useDeviceStatus();
  const { currentLanguage, setLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const title = t.sidebar[MENU_TKEY[activeMenu]] ?? '';
  const [showDeviceInfo, setShowDeviceInfo] = useState(false);

  return (
    <header className="tb">
      {/* ── 햄버거 (모바일 전용) ── */}
      {onMenuToggle && (
        <button
          type="button"
          className="tb__hamburger"
          onClick={onMenuToggle}
          aria-label={t.topbar.menuLabel}
        >
          <Menu size={20} strokeWidth={1.5} />
        </button>
      )}

      {/* ── Breadcrumb + 타이틀 ── */}
      <div className="flex flex-col gap-0.5 justify-center min-w-0">
        <div className="tb__breadcrumb">
          <span>LINK BAND SDK</span>
          <ChevronRight strokeWidth={1.5} size={10} className="opacity-40" />
          <span>{title}</span>
        </div>
        <div className="tb__title">{title}</div>
      </div>

      <div className="tb__spacer" />

      {/* ── 언어 토글 + 디바이스 상태 칩 + Sync ── */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* 언어 전환 토글 */}
        <div
          role="group"
          aria-label="Language"
          className="inline-flex items-center rounded-full bg-white/[0.04] border border-white/10 p-0.5"
        >
          <button
            type="button"
            onClick={() => setLanguage('ko')}
            className={
              'px-2.5 py-1 text-xs rounded-full transition-colors ' +
              (currentLanguage === 'ko'
                ? 'bg-indigo-500/80 text-white'
                : 'text-[var(--fg-3)] hover:text-[var(--fg-1)]')
            }
            aria-pressed={currentLanguage === 'ko'}
          >
            KO
          </button>
          <button
            type="button"
            onClick={() => setLanguage('en')}
            className={
              'px-2.5 py-1 text-xs rounded-full transition-colors ' +
              (currentLanguage === 'en'
                ? 'bg-indigo-500/80 text-white'
                : 'text-[var(--fg-3)] hover:text-[var(--fg-1)]')
            }
            aria-pressed={currentLanguage === 'en'}
          >
            EN
          </button>
        </div>

        {/* 디바이스 상태 — 아이콘 + 클릭 시 상세 확장 */}
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowDeviceInfo((v) => !v)}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
              isConnected
                ? 'text-green-400 bg-green-500/10 hover:bg-green-500/20'
                : 'text-[var(--fg-3)] bg-white/[0.04] hover:bg-white/[0.08]'
            }`}
            aria-label={isConnected ? t.deviceManager.deviceName : t.topbar.noDevice}
            title={isConnected ? (deviceName ?? 'LinkBand') : t.topbar.noDevice}
          >
            {isConnected ? <Bluetooth size={16} strokeWidth={1.5} /> : <BluetoothOff size={16} strokeWidth={1.5} />}
          </button>

          {showDeviceInfo && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowDeviceInfo(false)} />
              <div className="absolute right-0 top-full mt-2 w-60 z-50 rounded-lg border border-slate-700 bg-slate-900 shadow-xl p-3">
                {isConnected ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="lb-dot" />
                      <span className="text-sm font-medium text-white truncate">{deviceName ?? 'LinkBand'}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">{t.deviceManager.deviceId}</span>
                      <span className="font-mono text-slate-200">{currentDeviceId ?? '-'}</span>
                    </div>
                    {batteryLevel > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">{t.deviceManager.battery}</span>
                        <span className="text-green-400 font-medium">{Math.round(batteryLevel)}%</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-400">{t.topbar.noDevice}</div>
                )}
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => window.location.reload()}
          title="Sync"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md
                     hover:bg-white/[0.06] text-[var(--fg-3)] hover:text-[var(--fg-1)]
                     transition-colors duration-150 flex-shrink-0"
        >
          <RefreshCw strokeWidth={1.5} size={14} />
          <span className="hidden sm:inline">Sync</span>
        </button>
      </div>
    </header>
  );
}
